/**
 * A fixed, always-signed-in session.
 *
 * The demo has no auth server: `getSession()` always returns the same session,
 * and `signOut()` resolves without clearing it (signing out of a demo would
 * strand the visitor on a login screen no password can pass).
 *
 * The identity is a REAL profile row from the snapshot — the owner's own admin
 * account — so `AuthProvider`'s unchanged `profiles` lookup finds it, and every
 * "mine only" filter in the app (`assignee_user_id`, `completed_by`, …)
 * resolves to that user's real rows instead of an empty list.
 */

export const DEMO_USER_ID = 'b74357fc-39cc-4c00-a38f-efd96d505532';
export const DEMO_USER_EMAIL = 'tj.rozansky@pdqrestoration.com';
export const DEMO_DISPLAY_NAME = 'Tim Rozansky Jr';

const NOW_ISO = new Date(0).toISOString();

export type DemoUser = {
  id: string;
  aud: string;
  role: string;
  email: string;
  email_confirmed_at: string;
  phone: string;
  confirmed_at: string;
  last_sign_in_at: string;
  app_metadata: { provider: string; providers: string[] };
  user_metadata: Record<string, unknown>;
  identities: unknown[];
  created_at: string;
  updated_at: string;
  is_anonymous: boolean;
};

export type DemoSession = {
  access_token: string;
  refresh_token: string;
  token_type: string;
  expires_in: number;
  expires_at: number;
  user: DemoUser;
};

export const DEMO_USER: DemoUser = {
  id: DEMO_USER_ID,
  aud: 'authenticated',
  role: 'authenticated',
  email: DEMO_USER_EMAIL,
  email_confirmed_at: NOW_ISO,
  phone: '',
  confirmed_at: NOW_ISO,
  last_sign_in_at: NOW_ISO,
  app_metadata: { provider: 'email', providers: ['email'] },
  user_metadata: { display_name: DEMO_DISPLAY_NAME },
  identities: [],
  created_at: NOW_ISO,
  updated_at: NOW_ISO,
  is_anonymous: false,
};

/** Far-future expiry so nothing in the app decides the token went stale. */
export const DEMO_SESSION: DemoSession = {
  access_token: 'demo-access-token',
  refresh_token: 'demo-refresh-token',
  token_type: 'bearer',
  expires_in: 60 * 60 * 24 * 365,
  expires_at: Math.floor(Date.now() / 1000) + 60 * 60 * 24 * 365,
  user: DEMO_USER,
};

type AuthChangeCallback = (event: string, session: DemoSession | null) => void;

export type FakeAuth = ReturnType<typeof createFakeAuth>;

export function createFakeAuth() {
  return {
    getSession: async () => ({ data: { session: DEMO_SESSION }, error: null }),
    getUser: async () => ({ data: { user: DEMO_USER }, error: null }),
    getClaims: async () => ({ data: { claims: { sub: DEMO_USER_ID } }, error: null }),
    refreshSession: async () => ({ data: { session: DEMO_SESSION, user: DEMO_USER }, error: null }),
    setSession: async (_tokens?: unknown) => ({
      data: { session: DEMO_SESSION, user: DEMO_USER },
      error: null,
    }),
    signInWithPassword: async (_credentials?: unknown) => ({
      data: { session: DEMO_SESSION, user: DEMO_USER },
      error: null,
    }),
    signUp: async (_credentials?: unknown) => ({
      data: { session: DEMO_SESSION, user: DEMO_USER },
      error: null,
    }),
    signOut: async () => ({ error: null }),
    resetPasswordForEmail: async (_email?: string, _opts?: unknown) => ({ data: {}, error: null }),
    updateUser: async (_attrs?: unknown) => ({ data: { user: DEMO_USER }, error: null }),
    onAuthStateChange: (callback: AuthChangeCallback) => {
      // The real client emits asynchronously, after the caller has stored the
      // subscription handle. Match that.
      queueMicrotask(() => {
        try {
          callback('INITIAL_SESSION', DEMO_SESSION);
        } catch {
          // A throwing listener must not take the demo down.
        }
      });
      return {
        data: {
          subscription: {
            id: 'demo-auth-subscription',
            callback,
            unsubscribe: () => {},
          },
        },
      };
    },
  };
}
